import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const hueToGradient = (hue: number): string => {
  if (hue === 360) return 'hsl(0, 0%, 20%)';
  if (hue === 999) return 'hsl(220, 10%, 45%)';
  return `hsl(${hue}, 70%, 50%)`;
};

export const hueToGradientLight = (hue: number): string => {
  if (hue === 360) return 'hsl(0, 0%, 25%)';
  if (hue === 999) return 'hsl(220, 10%, 60%)';
  return `hsl(${hue}, 70%, 65%)`;
};

export const getCarGradient = (hue?: number): string => {
  const actualHue = hue ?? 200;
  return `linear-gradient(135deg, ${hueToGradientLight(actualHue)}, ${hueToGradient(actualHue)})`;
};

export const COLOR_GRADIENTS: Record<string, string> = {
  blue: 'from-blue-400 to-blue-600',
  red: 'from-red-400 to-red-600',
  green: 'from-green-400 to-green-600',
  yellow: 'from-yellow-400 to-yellow-600',
  purple: 'from-purple-400 to-purple-600',
  pink: 'from-pink-400 to-pink-600',
  orange: 'from-orange-400 to-orange-600',
  gray: 'from-gray-400 to-gray-600',
};

export type FuelType = 'petrol' | 'diesel' | 'hybrid' | 'electric' | 'lpg' | 'not_specified';

export const FUEL_TYPE_ICONS: Record<FuelType, string> = {
  petrol: '⛽',
  diesel: '⛽',
  hybrid: '🔋',
  electric: '⚡',
  lpg: '💨',
  not_specified: '❔'
};

const EMOJIS = ['🚗', '🚙', '🚕', '🚐', '🚚', '🏎️', '🚓', '🚑', '🚒', '🚎', '🛻', '🚜'];

export const getRandomEmoji = (): string => {
  return EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
};

export type Car = {
  id: string;
  user_id: string;
  slug: string;
  name: string;
  make: string;
  model: string;
  year: number;
  plate_number: string;
  mileage: number;
  image_url: string;
  image_url_thumb?: string | null;
  is_public: boolean;
  vin_number: string;
  fuel_type: FuelType;
  placeholder_color: string;
  placeholder_hue: number;
  use_image_icon: boolean;
  icon_image_mode: string;
  icon_image_index: number;
  icon_text: string;
  icon_text_color_hue: number;
  created_at: string;
  is_favorite?: boolean;
  is_owner?: boolean;
  favorite_count?: number; // From admin RPC
  owner_email?: string;    // For admin view
  last_entry_at?: string;  // From admin RPC
  updated_at?: string;     // From car table
  sort_order?: number;
  distance_unit?: 'km' | 'miles';
  volume_unit?: 'liters' | 'gallons';
  currency?: Currency;
  currency_symbol?: string;
  brand_logo_url?: string; // Added
  bought_at: string; // Updated from string | undefined to string because we made it mandatory
  purchase_expense_id?: string | null;
  purchase_price?: number;
  purchase_mileage?: number;
  expense_types?: string[]; // List of enabled expense types (keys)
  last_modified_by_email?: string | null;
  allowed_emails?: string[];
  share_locations?: boolean;
  is_archived?: boolean;
  sell_date?: string | null;
  sell_price?: number | null;
  sell_mileage?: number | null;
  ai_lifespan_km?: number | null;
  user_lifespan_override_km?: number | null;
  body_type?: string | null;
  drivetrain?: string | null;
  transmission?: string | null;
  engine_info?: string | null;
  practical_fit_score?: number | null;
  emotional_satisfaction_score?: number | null;
  reliability_anxiety_score?: number | null;
  mental_factors_updated_at?: string | null;
  ai_baseline_json?: any | null;
  user_adjusted_baseline_json?: any | null;
  accident_history?: 'none' | 'minor' | 'major' | null;
  structural_damage?: boolean | null;
  rust_level?: 'none' | 'minor' | 'visible' | 'severe' | null;
  switch_simulation_json?: any | null;
  estimated_yearly_km?: number | null;
  repair_analysis_json?: any | null;
  repair_diagnosis_params_json?: any | null;
  tasks_generated_at?: string | null;
};

export type CarTaskCategory = 'fluids' | 'filters' | 'engine' | 'brakes' | 'suspension' | 'transmission' | 'tyres' | 'electrical' | 'inspection' | 'other';
export type CarTaskActionType = 'inspect' | 'replace' | 'service';

export type CarTask = {
  id: string;
  car_id: string;
  user_id: string;
  name: string;
  description: string | null;
  category: CarTaskCategory | null;
  action_type: CarTaskActionType | string | null;
  interval_distance: number | null;
  interval_months: number | null;
  first_interval_distance: number | null;
  first_interval_months: number | null;
  last_performed_mileage: number | null;
  last_performed_date: string | null;
  is_distance_locked: boolean;
  ai_generated: boolean;
  history_confirmed: boolean | null;
  reminder_mileage: boolean;
  reminder_date: boolean;
  external_id: string | null;
  created_at: string;
  updated_at: string;
};

export type Reminder = {
  id: string;
  car_id: string;
  user_id: string;
  name: string | null;
  description: string | null;
  remind_date: string;
  remind_time: string;
  extra_reminders: boolean;
  repeat_yearly: boolean;
  is_active: boolean;
  is_seen: boolean;
  stop_token: string;
  note_id: string | null;
  part_id: string | null;
  car_task_id: string | null;
  created_at: string;
  updated_at: string;
};

export type MileageRecord = {
  id: string;
  car_id: string;
  mileage: number;
  recorded_at: string;
  created_at: string;
};

export type ExpenseType =
  | 'fuel'
  | 'car_wash'
  | 'electricity'
  | 'insurance'
  | 'parking'
  | 'maintenance'
  | 'repairs'
  | 'tyres'
  | 'parts'
  | 'taxes'
  | 'other'
  | 'info';

export type LogEntry = {
  id: string;
  car_id: string;
  user_id: string;
  date: string;
  mileage: number | null;
  car_value?: number | null;
  title: string | null;
  time_used?: boolean | null;
  location_id?: string | null;
  repeat_id?: string | null;
  linked_info_id?: string | null;
  linked_origin_id?: string | null;
  is_initial_purchase_entry?: boolean;
  created_at: string;
  // Included fields for convenience when joined natively
  expenses?: Expense[];
  location?: Location | null;
  imageCount?: number;
  partCount?: number;
};

export type Location = {
  id: string;
  user_id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  description: string | null;
  image_url: string | null;
  image_url_thumb: string | null;
  created_at: string;
  updated_at: string;
};

export type Expense = {
  id: string;
  car_id: string;
  log_entry_id?: string;
  date: string;
  type: ExpenseType;
  title: string;
  amount: number;
  amount_parts?: number;
  amount_work?: number;
  fuel_liters?: number;
  electricity_kwh?: number;
  fuel_type?: string;
  is_initial_purchase?: boolean;
  description: string;
  image_url: string;
  image_url_thumb?: string | null;
  ai_extracted: Record<string, any>;
  created_at: string;
  imageCount?: number;
  partCount?: number;
  last_modified_by_email?: string | null;
};

export type Note = {
  id: string;
  car_id: string;
  user_id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  is_public: boolean;
  next_due_date: string | null;
  created_at: string;
  imageCount?: number;
  partCount?: number;
  last_modified_by_email?: string | null;
};

export type Currency = 'EUR' | 'USD' | 'GBP' | 'JPY' | 'CAD' | 'AUD';

export type UserPreferences = {
  user_id: string;
  language: 'lv' | 'en' | 'de' | 'ru' | 'lt' | 'et' | 'es' | 'pt' | 'pl' | 'no' | 'sl' | 'sv' | 'ro' | 'nl' | 'it' | 'fr' | 'hr';
  theme: 'light' | 'dark';
  created_at: string;
  used_storage_bytes?: number;
  temp_storage_bytes?: number;
  storage_size_limit?: number;
  file_size_limit?: number;
  file_count_limit?: number;
  email_parsing_limit?: number;
  file_parsing_limit?: number;
  plan_type?: string;
  car_creation_limit?: number; // 0 = use plan default, >0 = individual override
  ai_daily_limit?: number;     // 0 = use plan default, >0 = individual override
  timezone?: string;
  time_format?: '24h' | '12h';
  api_token?: string;
  daily_api_calls_count?: number;
  daily_api_calls_reset_at?: string;
  hourly_api_calls_count?: number;
  hourly_api_calls_reset_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
};

export type Translation = {
  key: string;
  lv: string;
  en: string;
  category: string;
  updated_at: string;
};

export type ExpenseTypeRecord = {
  id: string;
  user_id: string;
  type_key: string;
  icon: string | null;
  color: string | null;
  sort_order: number | null;
  is_default?: boolean;
  name_lv?: string;
  name_en?: string;
  name_ru?: string;
  name_lt?: string;
  name_et?: string;
  name_es?: string;
  name_pt?: string;
  name_pl?: string;
  name_no?: string;
  name_sl?: string;
  name_sv?: string;
  name_ro?: string;
  name_nl?: string;
  name_it?: string;
  name_fr?: string;
  name_hr?: string;
  created_at: string;
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
};

export const EXPENSE_TYPE_LABELS: Record<ExpenseType, string> = {
  fuel: 'Fuel',
  car_wash: 'Wash',
  electricity: 'Electricity',
  insurance: 'Insurance',
  parking: 'Parking',
  maintenance: 'Maintenance',
  repairs: 'Repairs',
  tyres: 'Tyres',
  parts: 'Parts',
  taxes: 'Taxes',
  other: 'Other',
  info: 'Info',
};

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  electric: 'Electric',
  lpg: 'LPG',
  not_specified: 'Not Specified'
};
